import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@lf/analytics-events';
import { LoadingButton } from '@lf/ui-primitives';
import { childSignup } from '../api/lfApiChildLink';
import gradeOptions from '../lib/auth/gradeOptions';
import { readPendingShareToken } from '../lib/share/pendingShare';
import { useProfile } from '../contexts/ProfileContext';

function ChildSignupPage() {
    const { fetchProfile } = useProfile();
    const [form, setForm] = useState({
        first_name: '',
        grade_level: '',
        username: '',
        password: '',
        confirm_password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const requiredFieldsFilled = Boolean(
        form.username && form.password && form.confirm_password
    );
    const canSubmit = requiredFieldsFilled
        && form.password.length >= 4
        && form.password === form.confirm_password;

    // Single-step child signup form: fire signup_started on mount.
    useEffect(() => {
        track('signup_started', { step: 'child_setup', app_role: 'child' });
    }, []);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }

    async function handleSubmit(e) {
        if (e) e.preventDefault();

        setError('');

        if (!requiredFieldsFilled) {
            setError('Please fill all required fields.');
            track('form_submission_failed', {
                form: 'signup',
                field: 'required',
                reason: 'missing_required_fields',
            });
            return;
        }
        if (form.password.length < 4) {
            setError('Password must be at least 4 characters.');
            track('form_submission_failed', {
                form: 'signup',
                field: 'password',
                reason: 'too_short',
            });
            return;
        }
        if (form.password !== form.confirm_password) {
            setError('Passwords do not match.');
            track('form_submission_failed', {
                form: 'signup',
                field: 'confirm_password',
                reason: 'mismatch',
            });
            return;
        }

        setIsLoading(true);
        const result = await childSignup(
            form.username, form.password, form.first_name, form.grade_level);

        if (result && result.success) {
            if (result.coppa_age_check_required) {
                navigate('/age-check');
            } else {
                if (fetchProfile) {
                    await fetchProfile();
                }
                const shareToken = readPendingShareToken();
                if (shareToken) {
                    navigate(`/share/${shareToken}`);
                } else {
                    navigate('/home');
                }
            }
        } else {
            setError(result?.error || 'Signup failed. Please try again.');
            track('form_submission_failed', {
                form: 'signup',
                field: 'submit',
                reason: result?.error || 'unknown',
            });
        }
        setIsLoading(false);
    }

    return (
        <div className="flex mx-auto max-w-screen-lg my-8">
            <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-6 sm:px-20">
                <div className="text-sm mb-1">Welcome to Learnforest</div>
                <h2 className="text-lg mb-6">Create Student Account</h2>
                <form onSubmit={handleSubmit} className="flex flex-col items-center gap-2 w-full max-w-md">
                    <div className="relative w-full">
                        <input
                            type="text"
                            className="border border-gray-300 p-1 w-full"
                            name="username"
                            placeholder="Username"
                            value={form.username}
                            onChange={handleChange}
                        />
                        <span className="absolute right-[-1rem] top-1/2 -translate-y-1/2 text-red-500">*</span>
                    </div>
                    <div className="relative w-full">
                        <input
                            type="password"
                            className="border border-gray-300 p-1 w-full"
                            name="password"
                            placeholder="Password"
                            value={form.password}
                            onChange={handleChange}
                        />
                        <span className="absolute right-[-1rem] top-1/2 -translate-y-1/2 text-red-500">*</span>
                    </div>
                    <div className="relative w-full">
                        <input
                            type="password"
                            className="border border-gray-300 p-1 w-full"
                            name="confirm_password"
                            placeholder="Confirm password"
                            value={form.confirm_password}
                            onChange={handleChange}
                        />
                        <span className="absolute right-[-1rem] top-1/2 -translate-y-1/2 text-red-500">*</span>
                    </div>
                    <div className="w-full mt-4"></div>
                    <input
                        type="text"
                        className="border border-gray-300 p-1 w-full"
                        name="first_name"
                        placeholder="Display name (optional)"
                        value={form.first_name}
                        onChange={handleChange}
                    />
                    <select
                        className="border border-gray-300 p-1 w-full"
                        name="grade_level"
                        value={form.grade_level}
                        onChange={handleChange}
                    >
                        {gradeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    {error && <div className="text-red-500 text-sm mt-1">{error}</div>}

                    <LoadingButton
                        type="submit"
                        className="bg-blue-500 text-white py-2 px-4 rounded my-3"
                        text="Create Account"
                        isLoading={isLoading}
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    />

                    <a href="/" className="text-blue-500 underline text-sm mt-4">
                        Already have an account? Log in
                    </a>
                </form>
            </div>
        </div>
    );
}

export default ChildSignupPage;
